import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { routineService, workoutService } from '../services';
import { Play, Plus, Trash2 } from 'lucide-react';

const DAYS_MAP = {
  0: 'DOM',
  1: 'LUN',
  2: 'MAR',
  3: 'MIE',
  4: 'JUE',
  5: 'VIE',
  6: 'SAB',
};

const Dashboard = () => {
  const [todayRoutine, setTodayRoutine] = useState(null);
  const [allRoutines, setAllRoutines] = useState([]);
  const [recentWorkouts, setRecentWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const today = DAYS_MAP[new Date().getDay()];

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Get all routines
      const allRoutinesRes = await routineService.getAll();
      setAllRoutines(allRoutinesRes.data);
      
      // Find today's routine from all routines
      const todayRoutineData = allRoutinesRes.data.find(
        routine => routine.day_of_week === today
      );
      setTodayRoutine(todayRoutineData || null);

      // Get recent workouts
      const workoutsRes = await workoutService.getAll();
      setRecentWorkouts(workoutsRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartWorkout = async (routineId) => {
    try {
      const response = await workoutService.start({
        routine_id: routineId,
      });
      navigate(`/workout/${response.data.id}`);
    } catch (error) {
      console.error('Error starting workout:', error);
      alert('Error al iniciar el entrenamiento');
    }
  };

  const handleDeleteWorkout = async (e, workoutId) => {
    e.stopPropagation(); // Evitar que se active el click del card
    if (window.confirm('¿Estás seguro de eliminar este entrenamiento?')) {
      try {
        await workoutService.delete(workoutId);
        loadDashboardData(); // Recargar la lista
      } catch (error) {
        console.error('Error deleting workout:', error);
        alert('Error al eliminar el entrenamiento');
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-screen">
          <div className="text-center">
            <div className="inline-block w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-pulse"></div>
            <p className="text-xl text-gray-600 mt-4 font-semibold">Cargando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="animate-fadeIn">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-5xl font-extrabold mb-3 text-gray-900">
            Dashboard
          </h1>
          <div className="flex items-center space-x-2">
            <span className="text-2xl">📅</span>
            <p className="text-xl text-gray-700 font-medium">Hoy es <span className="font-bold text-indigo-700">{today}</span></p>
          </div>
        </div>

        {/* Today's Workout Card */}
        <div className="mb-8">
          {todayRoutine ? (
            <div className="gradient-primary rounded-2xl shadow-2xl p-8 md:p-12 text-white transform hover:scale-[1.02] transition-all">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="inline-block px-4 py-1 bg-white text-purple-600 rounded-full text-sm font-bold mb-3">
                    🎯 Rutina Recomendada para Hoy
                  </div>
                  <h2 className="text-4xl font-extrabold mb-2">
                    {todayRoutine.name}
                  </h2>
                  <p className="text-xl opacity-90">
                    {todayRoutine.routine_exercises?.length || 0} ejercicios listos para ti
                  </p>
                </div>
                <div className="text-6xl">💪</div>
              </div>
              <button
                onClick={() => handleStartWorkout(todayRoutine.id)}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-extrabold py-4 px-10 rounded-xl text-xl shadow-2xl transform hover:scale-105 transition-all flex items-center space-x-3"
              >
                <Play size={28} fill="white" />
                <span>INICIAR ENTRENAMIENTO</span>
              </button>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-lg p-8 md:p-12 text-center">
              <div className="text-6xl mb-4">😴</div>
              <h2 className="text-3xl font-bold mb-3 text-gray-800">
                Día de Descanso
              </h2>
              <p className="text-gray-600 mb-6 text-lg">
                No hay rutina programada para hoy. ¡Descansa o elige una rutina diferente!
              </p>
            </div>
          )}
        </div>

        {/* All Routines Section */}
        {allRoutines.length > 0 && (
          <div className="mb-8 bg-white rounded-2xl shadow-xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-gray-900 flex items-center">
                <span className="text-4xl mr-3">📋</span>
                Todas las Rutinas
              </h2>
              <button
                onClick={() => navigate('/routines')}
                className="bg-gradient-to-r from-cyan-400 to-blue-400 hover:from-cyan-500 hover:to-blue-500 text-gray-900 font-bold py-2 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
              >
                <Plus size={20} />
                <span>Nueva</span>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allRoutines.map((routine) => (
                <div
                  key={routine.id}
                  className="relative bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-200 hover:border-indigo-400 hover:shadow-xl transition-all group"
                >
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-indigo-700 transition">
                        {routine.name}
                      </h3>
                      <span className="text-2xl">💪</span>
                    </div>
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="inline-block px-3 py-1 bg-indigo-600 text-white text-xs font-bold rounded-full">
                        {routine.day_of_week}
                      </span>
                      {routine.day_of_week === today && (
                        <span className="inline-block px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                          ✨ Hoy
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 font-medium">
                      {routine.routine_exercises?.length || 0} ejercicios
                    </p>
                  </div>
                  <button
                    onClick={() => handleStartWorkout(routine.id)}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                  >
                    <Play size={20} fill="white" />
                    <span>Iniciar</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Workouts */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-900 flex items-center">
              <span className="text-4xl mr-3">📊</span>
              Historial Reciente
            </h2>
          </div>
          {recentWorkouts.length > 0 ? (
            <div className="grid gap-4">
              {recentWorkouts.map((workout) => (
                <div
                  key={workout.id}
                  className="card group relative"
                >
                  <div 
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => navigate(`/summary/${workout.id}`)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center text-2xl">
                        💪
                      </div>
                      <div>
                        <p className="font-bold text-lg text-gray-900 group-hover:text-purple-600 transition">
                          {workout.routine?.name || 'Entrenamiento Libre'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(workout.start_time).toLocaleDateString('es-ES', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center space-x-4">
                        {workout.total_time_seconds && (
                          <div className="text-center">
                            <p className="text-2xl font-bold text-purple-600">
                              {Math.floor(workout.total_time_seconds / 60)}
                            </p>
                            <p className="text-xs text-gray-500 font-medium">minutos</p>
                          </div>
                        )}
                        {workout.estimated_calories && (
                          <div className="text-center">
                            <p className="text-2xl font-bold text-orange-500">
                              {workout.estimated_calories}
                            </p>
                            <p className="text-xs text-gray-500 font-medium">kcal</p>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => handleDeleteWorkout(e, workout.id)}
                        className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors flex items-center justify-center"
                        title="Eliminar entrenamiento"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎯</div>
              <p className="text-gray-600 text-lg">
                Aún no has completado ningún entrenamiento
              </p>
              <p className="text-gray-500 mt-2">
                ¡Comienza tu primera rutina hoy!
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
